import type { DoctorShiftRule, TimeCondition, RuleType } from '../../domain/doctorShiftRule';
import { validateTimeConditions, canEditRule } from '../../domain/doctorShiftRule';
import type {
  DoctorShiftRuleRepository,
  UpdateDoctorShiftRuleData
} from './ports/DoctorShiftRuleRepository';

export interface UpdateDoctorShiftRuleInput {
  id: string;
  ruleType?: RuleType;
  timeConditions?: TimeCondition[];
}

export interface UpdateDoctorShiftRuleOutput {
  success: boolean;
  rule?: DoctorShiftRule;
  errors?: string[];
}

/**
 * 医師シフトルールを更新するUseCase
 * @param repository DoctorShiftRuleRepository リポジトリインスタンス
 * @param input UpdateDoctorShiftRuleInput 更新データ（ID、ルールタイプ、時間条件）
 * @returns Promise<UpdateDoctorShiftRuleOutput> 更新結果（成功時: rule、失敗時: errors）
 */
export async function updateDoctorShiftRule(
  repository: DoctorShiftRuleRepository,
  input: UpdateDoctorShiftRuleInput
): Promise<UpdateDoctorShiftRuleOutput> {
  const errors: string[] = [];

  // 既存ルール取得
  const existingRule = await repository.findById(input.id);
  if (!existingRule) {
    return { success: false, errors: ['指定されたルールが見つかりません'] };
  }

  // 編集可能チェック
  if (!canEditRule(existingRule)) {
    return { success: false, errors: ['このルールは編集できません（削除済み）'] };
  }

  // 時間条件バリデーション
  if (input.timeConditions) {
    const timeValidation = validateTimeConditions(input.timeConditions);
    if (!timeValidation.isValid) {
      errors.push(...timeValidation.errors);
    }
  }

  if (errors.length > 0) {
    return { success: false, errors };
  }

  const data: UpdateDoctorShiftRuleData = {};
  if (input.ruleType !== undefined) {
    data.ruleType = input.ruleType;
  }
  if (input.timeConditions !== undefined) {
    data.timeConditions = input.timeConditions;
  }

  const rule = await repository.update(input.id, data);

  return { success: true, rule };
}
